// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { ChildProps } from '@apollo/client/react/hoc';
import I18n from 'i18n-js';
import { get } from 'lodash';
import React, { Fragment } from 'react';

import {
  Core_Csp_HeaderQuery,
  Core_Csp_LayoutQueryQuery,
} from '@reverbdotcom/commons/src/gql/graphql';
import BestSellerBadge from '../../best_seller_badge';
import SellOrTradeInCTA from '../../SellOrTradeInCTA';
import URLHelpers, { withWebHostAndRegion } from '../../shared/url_helpers';
import { FavoriteButton } from '../../favorites/favorite_button';

import { RCImage } from '@reverbdotcom/cadence/components';
import Breadcrumbs from '@reverbdotcom/commons/src/components/breadcrumbs';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { orderedCategories } from '@reverbdotcom/commons/src/csp_category_helper';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { findProductFamilySpec } from '@reverbdotcom/commons/src/product_family_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

type Core_Csp_LayoutQueryQuery_Csp = Core_Csp_LayoutQueryQuery['csp'];

interface IProps extends IUserContext {
  csp: Core_Csp_LayoutQueryQuery_Csp;
  slug: string;
  soldListingId: string;
  ssrBestSellerBadge: boolean;
  canSaveSearch?: boolean;
}

function makeBreadcrumbClickEvent(cspId: string, breadcrumbUrl: string) {
  return {
    eventName: MParticleEventName.ClickedBreadcrumb,
    componentName: 'CspHeader',
    entityType: 'url',
    entityId: breadcrumbUrl,
    cspId,
  };
}

export class CSPHeader extends React.Component<ChildProps<IProps, Core_Csp_HeaderQuery>, null> {

  renderBrand() {
    const { brand } = this.props.csp;
    return (
      <>
        <CoreLink
          data-brand-link
          to={brand.webLink?.href}
          itemProp="item"
        >
          <span itemProp="name">
            {brand.name}
          </span>
        </CoreLink>
        <meta itemProp="position" content={ `${orderedCategories(this.props.csp).length + 1}` } />
      </>
    );
  }

  renderCategories() {
    return (
      orderedCategories(this.props.csp).map((cat, idx) => {
        return (
          <Fragment key={cat.id}>
            <CoreLink
              key={cat.id}
              data-category-link
              to={cat.cmsUrl.href}
              itemProp="item"
            >
              <span itemProp="name">
                {cat.name}
              </span>
            </CoreLink>
            <meta itemProp="position" content={ `${idx + 1}` } />
          </Fragment>
        );
      })
    );
  }

  renderProductFamily() {
    const productFamily = findProductFamilySpec(this.props.csp.specs?.productSpecs);
    if (!productFamily) return null;

    const clickEvent = {
      eventName: MParticleEventName.ClickedComponent,
      componentName: 'ProductFamilyCTA',
      entityType: 'url',
      entityId: productFamily.path,
    };

    return (
      <>
        <CoreLink
          to={productFamily.path}
          data-product-family-link
          clickEvent={clickEvent}
          target="_top"
        >
          <span itemProp="name">
            {productFamily.value}
          </span>
        </CoreLink>
        <meta itemProp="position" content={ `${orderedCategories(this.props.csp).length + 2}` } />
      </>
    );
  }

  renderBreadcrumbs() {
    const breadcrumbs = this.props.csp?.breadcrumbs;

    if (breadcrumbs) {
      return (
        <Breadcrumbs shouldScroll>
          <>
            <CoreLink
              to={{ pathname: '/' }}
            >
              <span itemProp="name">
                {I18n.t('commons.home')}
              </span>
            </CoreLink>
            <meta itemProp="position" content="1" />
            <meta itemProp="item" content={withWebHostAndRegion('/', this.props.user.urlRegion)} />
          </>
          {breadcrumbs.map((breadcrumb, index) => (
            <Fragment key={breadcrumb.url}>
              <CoreLink
                to={breadcrumb.url}
                target="_top"
                clickEvent={makeBreadcrumbClickEvent(this.props.csp?.id, breadcrumb.url)}
              >
                <span itemProp="name">
                  {breadcrumb.title}
                </span>
              </CoreLink>
              <meta itemProp="position" content={`${(index + 2)}`} />
              <meta itemProp="item" content={withWebHostAndRegion(breadcrumb.url, this.props.user.urlRegion)} />
            </Fragment>
          ))}
          <>
            <span itemProp="name">
              {this.props.csp.title}
            </span>
            <meta itemProp="position" content={`${(breadcrumbs.length + 2)}`} />
          </>
        </Breadcrumbs>
      );
    }

    return (
      <Breadcrumbs shouldScroll>
        {this.renderCategories()}
        {this.props.csp.brand && this.renderBrand()}
        {this.renderProductFamily()}
      </Breadcrumbs>
    );
  }

  renderEditButton() {
    if (!this.props.user.isAdmin) return null;

    const uri = URLHelpers.adminEditCspPath(this.props.slug);

    return (
      <a href={uri} data-admin-button className="ph ph-magic-wand color-orange ml-2 size-80" />
    );
  }

  renderSaveSearchButton() {
    return <div className="d-inline-block valign-bottom ml-3">
      <FavoriteButton
        condensed={true}
        renderWithBanner={false}
        componentName="FavoriteButton (Header)"
        size="small"
      />
    </div>;
  }

  soldListing() {
    return get(this.props, 'data.listing');
  }

  renderListingSold() {
    if (!this.soldListing()) return null;
    const { soldListingId } = this.props;

    trackEvent(
      {
        eventName: MParticleEventName.RedirectedToCSPFromEndedListing,
        listingId: soldListingId,
      },
    );

    // using a get here as rarely, sold or ended listings might not have an image
    const listingImage = get(this.soldListing(), 'images[0].source');
    const { title } = this.soldListing();

    return (
      <div className="csp2-header__listing-sold">
        <div className="csp2-header__listing-sold__inner">
          {listingImage &&
            <div className="csp2-header__listing-sold__image">
              <img src={listingImage} alt={title} />
            </div>
          }
          <div className="csp2-header__listing-sold__title">
            {I18n.t('discovery.csp2.listingSold')}
          </div>
        </div>
      </div>
    );
  }

  renderSellOrTradeInCTA() {
    return (
      <div className="tablet-d-none max-width-30">
        <SellOrTradeInCTA csp={this.props.csp} />
      </div>
    );
  }

  render() {
    const { csp, canSaveSearch, ssrBestSellerBadge } = this.props;
    if (!csp) { return null; }

    return (
      <div className="csp2-header">
        {this.renderListingSold()}
        <div className="csp2-header__breadcrumbs">
          {this.renderBreadcrumbs()}
        </div>
        <div className="csp2-header__inner">
          <div className="csp2-header__content">
            {csp.cspImage && csp.cspImage.length > 0 &&
              <div className="csp2-header__image">
                <RCImage
                  src={csp.cspImage[0].source}
                  alt={csp.title}
                  imageBox="auto"
                  overlay="offwhite"
                  borderRadius="md"
                />
              </div>
            }
            <div className="csp2-header__content__inner">
              <h1 className="csp2-header__title">
                {csp.title}
                {canSaveSearch && this.renderSaveSearchButton()}
                {this.renderEditButton()}
              </h1>
              <BestSellerBadge
                cspId={csp.id}
                categories={orderedCategories(csp)}
                ssr={ssrBestSellerBadge}
              />
            </div>
            {this.renderSellOrTradeInCTA()}
          </div>
        </div>
      </div>
    );
  }
}

export const CSP2HeaderFragment = gql`
  fragment CSP2HeaderData on CSP {
    _id
    id
    title
    brand {
      _id
      name
      webLink {
        href
      }
    }
    categories {
      _id
      name
      cmsUrl {
        href
      }
    }
    cspImage: images(input: {
      transform: "card_square"
      scope: "photos"
      limit: 1
    }) {
      _id
      source
    }
    specs {
      productSpecs {
        _id
        type
        name
        value
        path
      }
    }
  }
`;

const connect = withGraphql<IProps, Core_Csp_HeaderQuery>(
  gql`
    query Core_Csp_Header(
      $soldListingId: String
    ) {
      listing(
        input: {id: $soldListingId}
      ) {
        _id
        title
        images(input: {transform: "thumb"}) {
          _id
          source
        }
      }
    }
  `,
  {
    skip: ownProps => !ownProps.soldListingId,
    options: (ownProps) => {
      return {
        ssr: true,
        variables: {
          soldListingId: ownProps.soldListingId,
        },
      };
    },
  },
);

export default withUserContext<IProps>(connect(CSPHeader));
